import Fingerprint from '@mui/icons-material/Fingerprint'
import Google from '@mui/icons-material/Google'
import {
  Alert,
  Button,
  Container,
  Divider,
  Link,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { api } from '../api/client'
import type { ChallengeCreated } from '../api/generated/ChallengeCreated'
import type { EmailLoginRequest } from '../api/generated/EmailLoginRequest'
import type { GoogleRedirect } from '../api/generated/GoogleRedirect'
import type { LoginMethods } from '../api/generated/LoginMethods'
import type { Me } from '../api/generated/Me'
import type { SmsLoginRequest } from '../api/generated/SmsLoginRequest'
import { useAction } from '../api/useAction'
import { useApi } from '../api/useApi'
import { rememberNext, takeNext } from '../auth/next'
import { passkeysSupported } from '../auth/passkey'
import { useSession } from '../auth/session'
import { usePasskeyLogin } from '../auth/usePasskeyLogin'
import { PhoneField } from '../phone/PhoneField'

const methods = {
  email: {
    intro: 'Recevez un lien et un code de connexion par email.',
    field: { label: 'Email', type: 'email' as const, autoComplete: 'email webauthn' },
    submit: 'Recevoir le lien',
    request: (email: string) =>
      api.post<ChallengeCreated>('/auth/email', { email } satisfies EmailLoginRequest),
    sent: 'Email envoyé à',
    next: 'Ouvrez le lien, ou saisissez le code reçu.',
    change: "Changer d'adresse ou renvoyer un email",
  },
  sms: {
    intro: 'Recevez un code par SMS, si votre numéro est enregistré dans votre profil.',
    field: { label: 'Numéro de mobile', type: 'tel' as const, autoComplete: 'tel webauthn' },
    submit: 'Recevoir le code',
    request: (phone: string) =>
      api.post<ChallengeCreated>('/auth/sms', { phone } satisfies SmsLoginRequest),
    sent: 'SMS envoyé au',
    next: 'Saisissez le code reçu.',
    change: 'Changer de numéro ou renvoyer un SMS',
  },
}

type Method = keyof typeof methods

export function Login() {
  const { setMe } = useSession()
  const { route, query } = useLocation()
  const [method, setMethod] = useState<Method>('email')
  const [contact, setContact] = useState('')
  const [code, setCode] = useState('')
  const [challengeId, setChallengeId] = useState<string>()
  const { error, pending, run } = useAction()
  const google = useAction()
  const { data: loginMethods } = useApi<LoginMethods>('/auth/methods')
  const passkey = usePasskeyLogin((me: Me) => {
    setMe(me)
    route(takeNext(), true)
  })

  useEffect(() => rememberNext(query.next), [])

  const requestCode = (event: Event) => {
    event.preventDefault()
    run(async () => {
      const { challenge_id } = await methods[method].request(contact)
      setChallengeId(challenge_id)
      setCode('')
    })
  }

  const submitCode = (event: Event) => {
    event.preventDefault()
    run(async () => {
      setMe(await api.post<Me>('/auth/code', { challenge_id: challengeId, code }))
      route(takeNext(), true)
    })
  }

  const loginWithGoogle = () =>
    google.run(async () => {
      const { url } = await api.post<GoogleRedirect>('/auth/google/start', {})
      window.location.assign(url)
    })

  const chosen = methods[method]
  const passkeys = passkeysSupported()

  return (
    <Container maxWidth="xs" sx={{ py: 6 }}>
      <Typography variant="h4" gutterBottom>
        Connexion
      </Typography>
      {challengeId === undefined ? (
        <Stack component="form" spacing={2} onSubmit={requestCode}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={method}
            onChange={(_, value: Method | null) => {
              if (!value) return
              setMethod(value)
              setContact('')
            }}
          >
            <ToggleButton value="email">Email</ToggleButton>
            <ToggleButton value="sms">SMS</ToggleButton>
          </ToggleButtonGroup>
          <Typography>{chosen.intro}</Typography>
          {method === 'sms' ? (
            <PhoneField
              key={method}
              label={chosen.field.label}
              autoComplete={chosen.field.autoComplete}
              required
              autoFocus
              value={contact}
              onValue={setContact}
            />
          ) : (
            <TextField
              key={method}
              {...chosen.field}
              required
              autoFocus
              value={contact}
              onInput={(e) => setContact((e.target as HTMLInputElement).value)}
            />
          )}
          {error && <Alert severity="error">{error}</Alert>}
          <Button type="submit" variant="contained" size="large" loading={pending}>
            {chosen.submit}
          </Button>
          {(passkeys || loginMethods?.google) && <Divider>ou</Divider>}
          {loginMethods?.google && (
            <>
              {google.error && <Alert severity="error">{google.error}</Alert>}
              <Button
                variant="outlined"
                size="large"
                startIcon={<Google />}
                loading={google.pending}
                onClick={loginWithGoogle}
              >
                Continuer avec Google
              </Button>
            </>
          )}
          {passkeys && (
            <>
              {passkey.error && <Alert severity="error">{passkey.error}</Alert>}
              <Button
                variant="outlined"
                size="large"
                startIcon={<Fingerprint />}
                loading={passkey.pending}
                onClick={passkey.start}
              >
                Se connecter avec une clé d'accès
              </Button>
            </>
          )}
        </Stack>
      ) : (
        <Stack component="form" spacing={2} onSubmit={submitCode}>
          <Typography>
            {chosen.sent} <strong>{contact}</strong>. {chosen.next}
          </Typography>
          <TextField
            label="Code à 6 chiffres"
            required
            autoFocus
            autoComplete="one-time-code"
            slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9]{6}', maxLength: 6 } }}
            value={code}
            onInput={(e) => setCode((e.target as HTMLInputElement).value)}
          />
          {error && <Alert severity="error">{error}</Alert>}
          <Button type="submit" variant="contained" size="large" loading={pending}>
            Se connecter
          </Button>
          <Link component="button" type="button" onClick={() => setChallengeId(undefined)}>
            {chosen.change}
          </Link>
        </Stack>
      )}
      <Link href="/privacy" variant="body2" color="text.secondary" sx={{ display: 'block', mt: 4 }}>
        Règles de confidentialité
      </Link>
    </Container>
  )
}
